/*!
 * k47-orbit.js — Kepler-47-Simulation für 47*coffee
 * Custom Element <k47-orbit>, keine Abhängigkeiten.
 *
 * Attribute
 *   view="oblique|top"   Schrägansicht (Standard) oder Draufsicht
 *   speed="0.2"           Sekunden pro simuliertem Tag
 *   captions              Bildunterschriften (Fig., MET, Maßstab, Blickrichtung)
 *   sorten                Ebene „Sorten“: die sieben Sorten im System + Himmel über Greinbach
 *   sorten="stage"        nur die Sorten-Marken in der Grafik, ohne Seitenleiste
 *   readout               Telemetrie-Tabelle
 *   lightcurve            Lichtkurve mit Eklipsen und Transit-Marken
 *   paused                startet angehalten (bei prefers-reduced-motion automatisch)
 *   links='{"zenit":"/products/santos", …}'   Ziel-URLs der Sorten (JSON, optional)
 *   lat, lon, place       Beobachtungsort (Standard: Greinbach, 47,303° N, 15,973° O)
 *
 * Farben und Schriften kommen aus den Theme-Tokens:
 *   --paper --ink --ink-secondary --ink-muted --line --accent --focus --font-mono --sorte-*
 *
 * Daten: NASA Kepler-Mission (Umlaufzeiten, Massen, Exzentrizitäten, Planetenradien, Koordinaten J2000).
 * Bahnradien nach Keplers drittem Gesetz. Ausrichtung der Planetenbahnen und Startphasen frei gewählt.
 */
(() => {
  if (typeof window === 'undefined' || !window.customElements || customElements.get('k47-orbit')) return;

  const TAU = Math.PI * 2;
  const RAD = Math.PI / 180;
  const YEAR = 365.25;
  const R_SUN_AU = 0.00465047;
  const MA = 0.957; // Kepler-47 A, G6V
  const MB = 0.342; // Kepler-47 B, M4V
  const M = MA + MB;
  const axis = (P) => Math.cbrt(M * Math.pow(P / YEAR, 2)); // AE

  const BIN = { P: 7.4483648, e: 0.0288, w: 226.3 * RAD, phase0: 0.07 };
  BIN.a = axis(BIN.P);
  // Näherungswerte nur für die schematische Lichtkurve
  const RA_ = 0.964 * R_SUN_AU;
  const RB_ = 0.35 * R_SUN_AU;
  const LA = 0.84;
  const LB = 0.014;

  const PLANETS = [
    { id: 'b', label: '47 b', P: 49.4643, Re: 3.05, e: 0.021, w: 40 * RAD, phase0: 0.13 },
    { id: 'd', label: '47 d', P: 187.366, Re: 7.04, e: 0.024, w: 160 * RAD, phase0: 0.58 },
    { id: 'c', label: '47 c', P: 303.227, Re: 4.65, e: 0.044, w: 200 * RAD, phase0: 0.29, hz: true }
  ];
  PLANETS.forEach((p) => { p.a = axis(p.P); });
  const C = PLANETS[2];

  // Himmelskoordinaten Kepler-47 (J2000)
  const RA0 = (19 + 41 / 60 + 11.49832 / 3600) * 15;
  const DEC0 = 46 + 55 / 60 + 13.7073 / 3600;

  const SORTEN = [
    { key: 'zenit', no: '01', name: 'Zenit', where: 'Kepler-47 zieht fast genau durch den Zenit über Greinbach', color: '--sorte-zenit', fb: '#FFB35C', href: '/products/santos' },
    { key: 'eklipse', no: '02', name: 'Eklipse', where: 'Die beiden Sonnen verdecken sich gegenseitig', color: '--sorte-eklipse', fb: '#4FD8C4', href: '/products/santos-fancy-decaf' },
    { key: 'tandem', no: '03', name: 'Tandem', where: 'A + B: zwei Sonnen auf einer gemeinsamen Bahn', color: '--sorte-tandem', fb: '#7A6CFF', href: '/products/tandem' },
    { key: 'habitat', no: '04', name: 'Habitat', where: '47 c liegt in der lebensfreundlichen Zone', color: '--sorte-habitat', fb: '#9BDB6B', href: '/products/habitat' },
    { key: 'meridian', no: '05', name: 'Meridian', where: 'Die Nord-Süd-Linie, die Kepler-47 täglich überquert', color: '--sorte-meridian', fb: '#FF6E5E', alt: '--sorte-veracruz', href: '/products/mexico-veracruz' },
    { key: 'perihel', no: '06', name: 'Perihel', where: 'Sonnennächster Punkt der Bahn von 47 c', color: '--sorte-perihel', fb: '#F27BB4', href: '/products/perihel' },
    { key: 'aphel', no: '07', name: 'Aphel', where: 'Sonnenfernster Punkt der Bahn von 47 c', color: '--sorte-aphel', fb: '#6FB9FF', href: '/products/aphel' }
  ];

  const WINDOW = 40;
  const FLUX_MIN = 0.85;
  const FLUX_MAX = 1.012;

  const fmt = (d) => new Intl.NumberFormat('de-AT', { minimumFractionDigits: d, maximumFractionDigits: d });
  const f0 = fmt(0);
  const f1 = fmt(1);
  const f2 = fmt(2);
  const f3 = fmt(3);

  // Keplerbahn: Position im Bahnsystem, Brennpunkt im Ursprung
  function kepler(a, e, w, Mn) {
    let E = Mn + e * Math.sin(Mn);
    for (let i = 0; i < 4; i++) E -= (E - e * Math.sin(E) - Mn) / (1 - e * Math.cos(E));
    const x = a * (Math.cos(E) - e);
    const y = a * Math.sqrt(1 - e * e) * Math.sin(E);
    const cw = Math.cos(w);
    const sw = Math.sin(w);
    return { X: x * cw - y * sw, Y: x * sw + y * cw };
  }
  const binRel = (t) => kepler(BIN.a, BIN.e, BIN.w, TAU * (t / BIN.P + BIN.phase0));

  function positions(t) {
    const r = binRel(t);
    return {
      A: { X: -(MB / M) * r.X, Y: -(MB / M) * r.Y },
      B: { X: (MA / M) * r.X, Y: (MA / M) * r.Y },
      P: PLANETS.map((p) => kepler(p.a, p.e, p.w, TAU * (t / p.P + p.phase0)))
    };
  }

  function overlap(r1, r2, d) {
    if (d >= r1 + r2) return 0;
    if (d <= Math.abs(r1 - r2)) return Math.PI * Math.min(r1, r2) ** 2;
    const a1 = r1 * r1 * Math.acos((d * d + r1 * r1 - r2 * r2) / (2 * d * r1));
    const a2 = r2 * r2 * Math.acos((d * d + r2 * r2 - r1 * r1) / (2 * d * r2));
    const k = 0.5 * Math.sqrt((-d + r1 + r2) * (d + r1 - r2) * (d - r1 + r2) * (d + r1 + r2));
    return a1 + a2 - k;
  }

  // Relative Helligkeit für einen Beobachter in der Bahnebene (Blick entlang +Y)
  function flux(t) {
    const r = binRel(t);
    const d = Math.abs(r.X);
    if (d >= RA_ + RB_) return 1;
    const ov = overlap(RA_, RB_, d);
    const bFront = r.Y < 0;
    const loss = bFront ? (LA * ov) / (Math.PI * RA_ * RA_) : (LB * ov) / (Math.PI * RB_ * RB_);
    return 1 - loss / (LA + LB);
  }

  function nextEclipse(t) {
    let s0 = Math.sign(binRel(t).X);
    for (let dt = 0.01; dt < 5; dt += 0.01) {
      const s = Math.sign(binRel(t + dt).X);
      if (s !== s0) return dt;
    }
    return null;
  }
  const untilPhase = (t, p, target) => {
    const ph = ((t / p.P + p.phase0) % 1 + 1) % 1;
    return (((target - ph) % 1 + 1) % 1 || 1) * p.P;
  };

  // Himmel über dem Beobachtungsort (Echtzeit)
  function sky(lat, lon, ms) {
    const JD = ms / 86400000 + 2440587.5;
    const D = JD - 2451545.0;
    const yrs = D / YEAR;
    const ra = RA0 + (3.075 + 1.336 * Math.sin(RA0 * RAD) * Math.tan(DEC0 * RAD)) * yrs * 15 / 3600;
    const dec = DEC0 + (20.04 * Math.cos(RA0 * RAD) * yrs) / 3600;
    const gmst = ((280.46061837 + 360.98564736629 * D) % 360 + 360) % 360;
    const H = ((gmst + lon - ra) % 360 + 360) % 360;
    const p = lat * RAD;
    const d = dec * RAD;
    const h = H * RAD;
    const alt = Math.asin(Math.sin(p) * Math.sin(d) + Math.cos(p) * Math.cos(d) * Math.cos(h)) / RAD;
    const az = ((Math.atan2(-Math.cos(d) * Math.sin(h), Math.sin(d) * Math.cos(p) - Math.cos(d) * Math.cos(h) * Math.sin(p)) / RAD) % 360 + 360) % 360;
    const toCulm = (((360 - H) % 360) / 360.98564736629) * 86400000;
    return { ra, dec, H, alt, az, nextCulm: ms + toCulm, zenithDist: Math.abs(lat - dec), lowest: lat + dec - 90 };
  }
  function altAz(lat, dec, H) {
    const p = lat * RAD;
    const d = dec * RAD;
    const h = H * RAD;
    const alt = Math.asin(Math.sin(p) * Math.sin(d) + Math.cos(p) * Math.cos(d) * Math.cos(h)) / RAD;
    const az = Math.atan2(-Math.cos(d) * Math.sin(h), Math.sin(d) * Math.cos(p) - Math.cos(d) * Math.cos(h) * Math.sin(p)) / RAD;
    return { alt, az };
  }

  function parseColor(str) {
    const s = (str || '').trim();
    let m = s.match(/^#([0-9a-f]{3,8})$/i);
    if (m) {
      let h = m[1];
      if (h.length <= 4) h = h.split('').map((x) => x + x).join('');
      return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    }
    m = s.match(/rgba?\(([^)]+)\)/i);
    if (m) return m[1].split(/[ ,/]+/).slice(0, 3).map(Number);
    return null;
  }
  function isLight(str) {
    const c = parseColor(str);
    if (!c) return false;
    const [r, g, b] = c.map((v) => {
      const x = v / 255;
      return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.4;
  }

  const CSS = `
:host{display:block;container-type:inline-size;color:var(--ink,#F2EFE8);font-family:var(--font-mono,ui-monospace,Menlo,monospace)}
*{box-sizing:border-box}
[hidden]{display:none!important}
.wrap{display:grid;grid-template-columns:minmax(0,1fr)}
.stage{position:relative;margin:0;min-width:0}
canvas{display:block;width:100%}
.cap{position:absolute;font-size:11px;line-height:16px;letter-spacing:.55px;text-transform:uppercase;color:var(--ink-secondary,#A6A39C);pointer-events:none;white-space:nowrap}
.tl{top:14px;left:16px}.bl{bottom:14px;left:16px}.br{bottom:14px;right:16px}
.tr{position:absolute;top:6px;right:8px;display:flex;align-items:center;gap:12px}
.met{position:static}
.toggle{min-height:44px;min-width:44px;padding:0 14px;border:1px solid var(--line,#2B2B2B);border-radius:0;background:transparent;color:var(--ink,#F2EFE8);font:500 11px/16px var(--font-mono,ui-monospace,monospace);letter-spacing:.55px;text-transform:uppercase;cursor:pointer}
.toggle:hover{border-color:var(--ink,#F2EFE8)}
.toggle:focus-visible,a:focus-visible{outline:2px solid var(--focus,var(--accent,#FFB35C));outline-offset:2px}
.side{order:3;border-top:1px solid var(--line,#2B2B2B);min-width:0;display:grid;font-variant-numeric:tabular-nums}
.side>section{padding:16px;min-width:0}
.side>section+section{border-top:1px solid var(--line,#2B2B2B)}
.curve{order:2;border-top:1px solid var(--line,#2B2B2B);padding:14px 16px 10px;min-width:0}
.head{display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;font-size:11px;line-height:16px;letter-spacing:.55px;text-transform:uppercase;color:var(--ink-secondary,#A6A39C);margin-bottom:8px}
.head b{color:var(--ink,#F2EFE8);font-weight:500}
.lc{height:96px}
.skyrow{display:grid;grid-template-columns:minmax(0,190px) minmax(0,1fr);gap:16px;align-items:center}
.skyc{max-width:190px;aspect-ratio:1/1;height:auto}
table{width:100%;border-collapse:collapse;font-size:12px;line-height:18px;letter-spacing:.3px}
th{font-weight:400;text-transform:uppercase;font-size:11px;letter-spacing:.55px;color:var(--ink-secondary,#A6A39C);text-align:right;padding:6px 0 6px 10px;border-bottom:1px solid var(--ink,#F2EFE8)}
th:first-child,td:first-child{text-align:left;padding-left:0}
td{text-align:right;padding:9px 0 9px 10px;border-bottom:1px solid var(--line,#2B2B2B);vertical-align:top;font-weight:500;white-space:nowrap}
td small{display:block;font-size:10.5px;line-height:15px;font-weight:400;color:var(--ink-secondary,#A6A39C);white-space:normal;letter-spacing:.3px}
.legend td:first-child{white-space:normal}
.legend a{color:var(--ink,#F2EFE8);text-decoration:none;display:inline-flex;align-items:center;gap:8px;min-height:24px}
.legend a:hover .nm{text-decoration:underline;text-underline-offset:3px}
.sq{display:inline-block;width:8px;height:8px;flex:none}
.no{color:var(--ink-secondary,#A6A39C);font-weight:400}
.nm{text-transform:uppercase;letter-spacing:.6px}
.legend td:last-child{color:var(--ink,#F2EFE8)}
dl{display:grid;grid-template-columns:auto 1fr;gap:6px 16px;margin:0;font-size:11px;line-height:16px;letter-spacing:.55px;text-transform:uppercase}
dt{color:var(--ink-secondary,#A6A39C)}dd{margin:0;text-align:right;font-weight:500;font-variant-numeric:tabular-nums}
.telemetry dl{margin-top:14px}
@container (min-width: 900px){
  .wrap.has-side{grid-template-columns:minmax(0,1.75fr) minmax(320px,1fr)}
  .wrap.has-side .stage{grid-row:1;grid-column:1}
  .wrap.has-side .side{grid-row:1;grid-column:2;border-top:0;border-left:1px solid var(--line,#2B2B2B);align-content:start}
  .wrap.has-side .curve{grid-row:2;grid-column:1 / -1}
}
@container (max-width: 520px){ .tl,.bl{display:none} .skyrow{grid-template-columns:1fr} .skyc{margin:0 auto} }
`;

  class K47Orbit extends HTMLElement {
    static get observedAttributes() { return ['view', 'speed', 'captions', 'readout', 'lightcurve', 'sorten', 'paused', 'links', 'lat', 'lon', 'place']; }

    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      this.t = 0;
      this.running = false;
      this.visible = true;
      this.samples = [];
      this.events = [];
      this.counts = { eclipses: 0, transits: 0 };
      this.lastTransit = null;
      this.colors = {};
      this._flashUntil = 0;
      this._frame = this._frame.bind(this);
    }

    get view() { return this.getAttribute('view') === 'top' ? 'top' : 'oblique'; }
    get speed() { const v = parseFloat(this.getAttribute('speed')); return v > 0 ? v : 0.2; }
    get lat() { const v = parseFloat(this.getAttribute('lat')); return Number.isFinite(v) ? v : 47.30306; }
    get lon() { const v = parseFloat(this.getAttribute('lon')); return Number.isFinite(v) ? v : 15.97278; }
    get place() { return this.getAttribute('place') || 'Greinbach'; }

    connectedCallback() {
      if (!this._built) this._build();
      this._readColors();
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.userPaused = this.hasAttribute('paused') || reduce;
      this._prefill();
      this._layout();
      this._ro = new ResizeObserver(() => { this._layout(); this._draw(); this._drawSkyDome(); });
      this._ro.observe(this.$stage);
      this._ro.observe(this.$curve);
      this._ro.observe(this.$side);
      this._io = new IntersectionObserver((es) => { this.visible = es.some((e) => e.isIntersecting); this._sync(); });
      this._io.observe(this);
      this._onVis = () => this._sync();
      document.addEventListener('visibilitychange', this._onVis);
      this._mq = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
      this._onTheme = () => { this._readColors(); this._draw(); this._drawSkyDome(); };
      if (this._mq) this._mq.addEventListener('change', this._onTheme);
      this._mo = new MutationObserver(this._onTheme);
      this._mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
      this._colorTimer = setInterval(() => this._readColors(), 1500);
      this._skyTimer = setInterval(() => { if (!this.$skyblock.hidden) { this._drawSkyDome(); this._text(true); } }, 10000);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { this._draw(); this._drawSkyDome(); });
      this._draw();
      this._drawSkyDome();
      this._sync();
    }

    disconnectedCallback() {
      this._stop();
      if (this._ro) this._ro.disconnect();
      if (this._io) this._io.disconnect();
      if (this._mo) this._mo.disconnect();
      if (this._mq) this._mq.removeEventListener('change', this._onTheme);
      document.removeEventListener('visibilitychange', this._onVis);
      clearInterval(this._colorTimer);
      clearInterval(this._skyTimer);
    }

    attributeChangedCallback(name) {
      if (!this._built) return;
      if (name === 'paused') this.userPaused = this.hasAttribute('paused');
      if (name === 'links' || name === 'place') this._buildLegend();
      this._applyParts();
      this._layout();
      this._draw();
      this._drawSkyDome();
      this._sync();
    }

    pause() { this.userPaused = true; this._sync(); }
    play() { this.userPaused = false; this._sync(); }

    _links() {
      let custom = {};
      try { custom = JSON.parse(this.getAttribute('links') || '{}') || {}; } catch (e) { custom = {}; }
      return custom;
    }

    _build() {
      this._built = true;
      const root = this.shadowRoot;
      root.innerHTML = `<style>${CSS}</style>
<div class="wrap">
  <figure class="stage">
    <canvas class="sky" role="img" aria-label="Simulation des Doppelsternsystems Kepler-47: zwei Sonnen umkreisen einander im Zentrum, drei Planeten umkreisen beide."></canvas>
    <div class="cap tl">Fig. 00 — Kepler-47 · Simulation</div>
    <div class="tr"><span class="cap met"></span><button class="toggle" type="button"></button></div>
    <div class="cap bl">Abstände maßstäblich · Körper vergrößert</div>
    <div class="cap br">↓ Erde · 3.420 Lj.</div>
  </figure>
  <div class="side">
    <section class="skyblock">
      <div class="head"><span><b>Über <span class="pl"></span> //</b> Himmel jetzt</span><span>Blick nach oben · N oben</span></div>
      <div class="skyrow">
        <canvas class="skyc" role="img"></canvas>
        <dl class="skydl"></dl>
      </div>
    </section>
    <section class="legendblock">
      <table class="legend">
        <thead><tr><th scope="col">Sorte</th><th scope="col">Jetzt</th></tr></thead>
        <tbody></tbody>
      </table>
    </section>
    <section class="telemetry">
      <table>
        <thead><tr><th scope="col">Körper</th><th scope="col">Periode</th><th scope="col">Bahn</th><th scope="col">Umläufe</th></tr></thead>
        <tbody class="tel"></tbody>
      </table>
      <dl class="teldl"></dl>
    </section>
  </div>
  <div class="curve">
    <div class="head"><span><b>Lichtkurve //</b> relative Helligkeit</span><span>letzte ${WINDOW} d</span></div>
    <canvas class="lc" role="img" aria-label="Lichtkurve der letzten ${WINDOW} Tage: Einbrüche bei jeder gegenseitigen Bedeckung der Sonnen, Marken bei Planetentransits."></canvas>
  </div>
</div>`;
      const q = (s) => root.querySelector(s);
      this.$wrap = q('.wrap');
      this.$stage = q('.stage');
      this.$sky = q('.sky');
      this.$side = q('.side');
      this.$skyblock = q('.skyblock');
      this.$legendblock = q('.legendblock');
      this.$telemetry = q('.telemetry');
      this.$skyc = q('.skyc');
      this.$skydl = q('.skydl');
      this.$legend = q('.legend tbody');
      this.$curve = q('.curve');
      this.$lc = q('.lc');
      this.$met = q('.met');
      this.$toggle = q('.toggle');
      this.$caps = root.querySelectorAll('.tl,.bl,.br,.met');
      this.$toggle.addEventListener('click', () => { this.userPaused = !this.userPaused; this._sync(); });

      const rows = [
        { key: 'bin', name: 'A + B', sub: 'G6V + M4V · Doppelstern', P: BIN.P, a: BIN.a, pf: f2 },
        ...PLANETS.map((p) => ({ key: p.id, name: p.label, sub: `${f1.format(p.Re)} R⊕ · e ${f3.format(p.e)}${p.hz ? ' · lebensfreundliche Zone' : ''}`, P: p.P, a: p.a }))
      ];
      q('.tel').innerHTML = rows.map((r) => `<tr><td>${r.name}<small>${r.sub}</small></td><td>${(r.pf || f1).format(r.P)} d</td><td>${(r.a < 0.1 ? f3 : f2).format(r.a)} AE</td><td data-k="${r.key}"></td></tr>`).join('');
      this.$orbits = {};
      root.querySelectorAll('.tel td[data-k]').forEach((td) => { this.$orbits[td.dataset.k] = td; });
      q('.teldl').innerHTML = '<dt>MET</dt><dd data-v="met"></dd><dt>Eklipsen A/B</dt><dd data-v="ecl"></dd><dt>Transits</dt><dd data-v="tr"></dd><dt>Letzter Transit</dt><dd data-v="last"></dd><dt>Tempo</dt><dd data-v="speed"></dd>';
      this.$vals = {};
      root.querySelectorAll('.teldl dd').forEach((dd) => { this.$vals[dd.dataset.v] = dd; });
      this.$skydl.innerHTML = '<dt>Höhe jetzt</dt><dd data-s="alt"></dd><dt>Meridian</dt><dd data-s="culm"></dd><dt>Höchststand</dt><dd data-s="max"></dd><dt>Tiefststand</dt><dd data-s="min"></dd>';
      this.$sky_ = {};
      this.$skydl.querySelectorAll('dd').forEach((dd) => { this.$sky_[dd.dataset.s] = dd; });
      this._buildLegend();
      this._applyParts();
    }

    _buildLegend() {
      const links = this._links();
      this.shadowRoot.querySelector('.pl').textContent = this.place;
      this.$legend.innerHTML = SORTEN.map((s) => {
        const href = links[s.key] || s.href;
        return `<tr><td><a href="${href}" data-sorte="${s.key}"><span class="sq" data-sq="${s.key}"></span><span><span class="no">№ ${s.no}</span> <span class="nm">${s.name}</span></span></a><small>${s.where.replace('Greinbach', this.place)}</small></td><td data-live="${s.key}"></td></tr>`;
      }).join('');
      this.$live = {};
      this.$legend.querySelectorAll('td[data-live]').forEach((td) => { this.$live[td.dataset.live] = td; });
      this._paintSquares();
    }

    _paintSquares() {
      if (!this.$legend) return;
      this.$legend.querySelectorAll('[data-sq]').forEach((el) => { el.style.background = this._sorteColor(el.dataset.sq); });
    }

    _applyParts() {
      const readout = this.hasAttribute('readout');
      const sorten = this.hasAttribute('sorten') && this.getAttribute('sorten') !== 'stage';
      const curve = this.hasAttribute('lightcurve');
      const caps = this.hasAttribute('captions');
      this.$telemetry.hidden = !readout;
      this.$skyblock.hidden = !sorten;
      this.$legendblock.hidden = !sorten;
      this.$side.hidden = !(readout || sorten);
      this.$curve.hidden = !curve;
      this.$wrap.classList.toggle('has-side', readout || sorten);
      this.$caps.forEach((el) => { el.hidden = !caps; });
      this.$skyc.setAttribute('aria-label', `Himmel über ${this.place}: Kepler-47 zieht einmal am Tag fast genau durch den Zenit und geht nie unter.`);
    }

    _sorteColor(key) {
      const s = SORTEN.find((x) => x.key === key);
      if (!s) return '#FFB35C';
      const cs = getComputedStyle(this);
      return (cs.getPropertyValue(s.color) || '').trim() || (s.alt && (cs.getPropertyValue(s.alt) || '').trim()) || s.fb;
    }

    _readColors() {
      const cs = getComputedStyle(this);
      const v = (n, fb) => (cs.getPropertyValue(n) || '').trim() || fb;
      const c = {
        paper: v('--paper', '#0A0A0A'),
        ink: v('--ink', '#F2EFE8'),
        ink2: v('--ink-secondary', '#A6A39C'),
        muted: v('--ink-muted', '#76736C'),
        line: v('--line', '#2B2B2B'),
        accent: v('--accent', '#FFB35C'),
        mono: v('--font-mono', 'ui-monospace, Menlo, monospace')
      };
      SORTEN.forEach((s) => { c[s.key] = this._sorteColor(s.key); });
      c.light = isLight(c.paper);
      const changed = JSON.stringify(c) !== JSON.stringify(this.colors);
      this.colors = c;
      if (changed) {
        this._paintSquares();
        if (!this.running) { this._draw(); this._drawSkyDome(); }
      }
    }

    _advance(dt, record) {
      let remaining = dt;
      while (remaining > 1e-9) {
        const step = Math.min(remaining, 0.05);
        const prev = this._pos || positions(this.t);
        const t2 = this.t + step;
        const next = positions(t2);
        if (Math.sign(prev.B.X - prev.A.X) !== Math.sign(next.B.X - next.A.X)) {
          if (record) {
            this.counts.eclipses += 1;
            if (next.B.Y < next.A.Y) this._flashUntil = performance.now() + 480; // Hauptbedeckung: B vor A
          }
        }
        PLANETS.forEach((p, i) => {
          [['A', prev.A, next.A], ['B', prev.B, next.B]].forEach(([star, s0, s1]) => {
            const front = next.P[i].Y < s1.Y;
            if (front && Math.sign(prev.P[i].X - s0.X) !== Math.sign(next.P[i].X - s1.X)) {
              this.events.push({ t: t2, planet: p.id, star });
              if (record) {
                this.counts.transits += 1;
                this.lastTransit = { t: t2, planet: p.label, star };
              }
            }
          });
        });
        this.t = t2;
        this._pos = next;
        remaining -= step;
      }
      const dts = this._sampleStep || 0.04;
      let last = this.samples.length ? this.samples[this.samples.length - 1].t : this.t - WINDOW;
      while (last + dts <= this.t) {
        last += dts;
        this.samples.push({ t: last, f: flux(last) });
      }
      const cutoff = this.t - WINDOW - 1;
      while (this.samples.length && this.samples[0].t < cutoff) this.samples.shift();
      while (this.events.length && this.events[0].t < cutoff) this.events.shift();
    }

    _prefill() {
      this.t = -WINDOW;
      this._pos = null;
      this.samples = [];
      this.events = [];
      this._sampleStep = 0.04;
      this._advance(WINDOW, false);
      this.counts = { eclipses: 0, transits: 0 };
      this.lastTransit = null;
    }

    _layout() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = this.$stage.clientWidth || 600;
      const top = this.view === 'top';
      const h = Math.round(Math.max(260, Math.min(top ? 640 : 600, w * (top ? 0.92 : 0.62))));
      this.$sky.style.height = h + 'px';
      this.$sky.width = Math.round(w * dpr);
      this.$sky.height = Math.round(h * dpr);
      this.sky = { w, h, dpr };
      if (!this.$curve.hidden) {
        const cw = this.$lc.clientWidth || w;
        const ch = 96;
        this.$lc.width = Math.round(cw * dpr);
        this.$lc.height = Math.round(ch * dpr);
        this.lc = { w: cw, h: ch, dpr };
        this._sampleStep = Math.max(0.02, WINDOW / (Math.max(cw, 200) * 1.6));
      }
      if (!this.$skyblock.hidden) {
        const sw = this.$skyc.clientWidth || 190;
        this.$skyc.width = Math.round(sw * dpr);
        this.$skyc.height = Math.round(sw * dpr);
        this.dome = { w: sw, h: sw, dpr };
      }
    }

    _sync() {
      const should = !this.userPaused && this.visible && document.visibilityState !== 'hidden';
      this.$toggle.textContent = this.userPaused ? 'Weiter' : 'Pause';
      this.$toggle.setAttribute('aria-label', this.userPaused ? 'Simulation fortsetzen' : 'Simulation anhalten');
      if (should && !this.running) {
        this.running = true;
        this._last = performance.now();
        this._raf = requestAnimationFrame(this._frame);
      } else if (!should) {
        this._stop();
      }
      this._text(true);
    }

    _stop() {
      this.running = false;
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = null;
    }

    _frame(now) {
      if (!this.running) return;
      const realDt = Math.min(0.1, (now - this._last) / 1000);
      this._last = now;
      this._advance(realDt / this.speed, true);
      this._draw();
      this._text(false);
      this._raf = requestAnimationFrame(this._frame);
    }

    _met(t) {
      const tt = Math.max(0, t);
      const whole = Math.floor(tt);
      return `MET ${String(whole).padStart(4, '0')},${Math.floor((tt - whole) * 10)} d`;
    }

    _text(force) {
      const now = performance.now();
      if (!force && this._lastText && now - this._lastText < 150) return;
      this._lastText = now;
      const t = Math.max(0, this.t);
      this.$met.textContent = this._met(t);
      if (!this.$telemetry.hidden) {
        this.$orbits.bin.textContent = f1.format(t / BIN.P);
        PLANETS.forEach((p) => { this.$orbits[p.id].textContent = f2.format(t / p.P); });
        this.$vals.met.textContent = `${f1.format(t)} d`;
        this.$vals.ecl.textContent = f0.format(this.counts.eclipses);
        this.$vals.tr.textContent = f0.format(this.counts.transits);
        this.$vals.last.textContent = this.lastTransit ? `${this.lastTransit.planet} vor ${this.lastTransit.star} · ${f1.format(this.lastTransit.t)} d` : '—';
        this.$vals.speed.textContent = `1 d = ${f2.format(this.speed).replace(/0$/, '')} s`;
      }
      if (!this.$legendblock.hidden) {
        const s = sky(this.lat, this.lon, Date.now());
        const tf = new Intl.DateTimeFormat('de-AT', { timeZone: 'Europe/Vienna', weekday: 'short', hour: '2-digit', minute: '2-digit' });
        const culm = s.nextCulm - Date.now() < 60000 ? 'jetzt' : tf.format(new Date(s.nextCulm)).replace(',', '');
        const ecl = nextEclipse(this.t);
        this.$live.zenit.textContent = `${f1.format(90 - s.alt)}° vom Zenit`;
        this.$live.eklipse.textContent = ecl == null ? '—' : `in ${f1.format(ecl)} d`;
        this.$live.tandem.textContent = `${f2.format(BIN.P)} d`;
        this.$live.habitat.textContent = `${f2.format(C.a)} AE`;
        this.$live.meridian.textContent = culm;
        this.$live.perihel.textContent = `in ${f0.format(untilPhase(this.t, C, 0))} d`;
        this.$live.aphel.textContent = `in ${f0.format(untilPhase(this.t, C, 0.5))} d`;
        this.$sky_.alt.textContent = `${f1.format(s.alt)}°`;
        this.$sky_.culm.textContent = culm;
        this.$sky_.max.textContent = `${f1.format(90 - s.zenithDist)}° · ${f1.format(s.zenithDist)}° vom Zenit`;
        this.$sky_.min.textContent = `${f1.format(s.lowest)}° · geht nie unter`;
      }
    }

    _draw() {
      if (!this.sky) return;
      this._drawSystem();
      if (!this.$curve.hidden && this.lc) this._drawCurve();
    }

    // kleines Quadrat in Sortenfarbe + Text in Tinte
    _tag(ctx, key, text, x, y, align) {
      const c = this.colors;
      ctx.font = `500 11px ${c.mono}`;
      const tw = ctx.measureText(text).width;
      const total = 8 + 6 + tw;
      const x0 = align === 'right' ? x - total : x;
      ctx.fillStyle = c[key];
      ctx.fillRect(x0, Math.round(y - 4), 8, 8);
      ctx.fillStyle = c.ink;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      ctx.fillText(text, x0 + 14, y);
      return total;
    }

    _drawSystem() {
      const { w, h, dpr } = this.sky;
      const ctx = this.$sky.getContext('2d');
      const c = this.colors;
      const sorten = this.hasAttribute('sorten');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      const top = this.view === 'top';
      const narrow = w < 520;
      const squash = top ? 1 : 0.34;
      const rot = top ? 0 : (-11 * Math.PI) / 180;
      const cosR = Math.cos(rot);
      const sinR = Math.sin(rot);
      const aMax = C.a * (1 + C.e);
      const marginX = w < 520 ? 34 : (sorten ? 84 : 72);
      const marginY = top ? 46 : 42;
      const hx = Math.sqrt((aMax * cosR) ** 2 + (aMax * squash * sinR) ** 2);
      const hy = Math.sqrt((aMax * sinR) ** 2 + (aMax * squash * cosR) ** 2);
      const scale = Math.min((w / 2 - marginX) / hx, (h / 2 - marginY) / hy);
      const cx = w / 2;
      const cy = h / 2 + (top ? 0 : 6);
      const proj = (X, Y) => {
        const x0 = X * scale;
        const y0 = -Y * scale * squash;
        return [cx + x0 * cosR - y0 * sinR, cy + x0 * sinR + y0 * cosR];
      };
      const orbitPath = (a, e, w0, scaleF) => {
        ctx.beginPath();
        for (let i = 0; i <= 120; i++) {
          const E = (i / 120) * TAU;
          const x = a * (Math.cos(E) - e) * scaleF;
          const y = a * Math.sqrt(1 - e * e) * Math.sin(E) * scaleF;
          const X = x * Math.cos(w0) - y * Math.sin(w0);
          const Y = x * Math.sin(w0) + y * Math.cos(w0);
          const [px, py] = proj(X, Y);
          if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
      };

      // Bahnen
      PLANETS.forEach((p) => {
        orbitPath(p.a, p.e, p.w, 1);
        ctx.lineWidth = 1;
        ctx.strokeStyle = p.hz ? c.muted : c.line;
        ctx.setLineDash([]);
        ctx.stroke();
      });
      ctx.setLineDash([1, 3]);
      orbitPath(BIN.a, BIN.e, BIN.w + Math.PI, MB / M); ctx.strokeStyle = c.line; ctx.stroke();
      orbitPath(BIN.a, BIN.e, BIN.w, MA / M); ctx.stroke();
      ctx.setLineDash([]);

      // Schwerpunkt
      ctx.strokeStyle = c.muted;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(cx - 9, cy); ctx.lineTo(cx + 9, cy);
      ctx.moveTo(cx, cy - 9); ctx.lineTo(cx, cy + 9);
      ctx.stroke();

      // Perihel / Aphel von 47 c
      if (sorten) {
        const peri = proj(C.a * (1 - C.e) * Math.cos(C.w), C.a * (1 - C.e) * Math.sin(C.w));
        const aph = proj(-C.a * (1 + C.e) * Math.cos(C.w), -C.a * (1 + C.e) * Math.sin(C.w));
        [[peri, 'perihel', narrow ? 'Perihel' : `Perihel · ${f2.format(C.a * (1 - C.e))} AE`], [aph, 'aphel', narrow ? 'Aphel' : `Aphel · ${f2.format(C.a * (1 + C.e))} AE`]].forEach(([pt, key, text]) => {
          ctx.save();
          ctx.translate(pt[0], pt[1]);
          ctx.rotate(Math.PI / 4);
          ctx.fillStyle = c[key];
          ctx.fillRect(-4, -4, 8, 8);
          ctx.restore();
          ctx.font = `500 11px ${c.mono}`;
          const tw = ctx.measureText(text).width;
          const leftSide = pt[0] < cx;
          const lx = leftSide ? Math.max(4, pt[0] - 12 - tw) : Math.min(w - tw - 4, pt[0] + 12);
          const ly = pt[1] + (pt[1] < cy ? -14 : 14);
          ctx.fillStyle = c.ink;
          ctx.textBaseline = 'middle';
          ctx.textAlign = 'left';
          ctx.fillText(text, lx, ly);
        });
      }

      const pos = positions(this.t);
      const bodies = [
        { kind: 'star', id: 'A', X: pos.A.X, Y: pos.A.Y, r: 6.5 },
        { kind: 'star', id: 'B', X: pos.B.X, Y: pos.B.Y, r: 4 },
        ...PLANETS.map((p, i) => ({ kind: 'planet', p, X: pos.P[i].X, Y: pos.P[i].Y, r: 1.4 + p.Re * 0.45 }))
      ].sort((a, b) => b.Y - a.Y);

      bodies.forEach((b) => {
        const [x, y] = proj(b.X, b.Y);
        if (b.kind === 'star') {
          ctx.globalAlpha = c.light ? 0.28 : 0.2;
          ctx.fillStyle = c.accent;
          ctx.beginPath(); ctx.arc(x, y, b.r * 2.3, 0, TAU); ctx.fill();
          ctx.globalAlpha = 1;
          ctx.beginPath(); ctx.arc(x, y, b.r, 0, TAU);
          ctx.fillStyle = c.accent; ctx.fill();
          if (c.light) { ctx.lineWidth = 1; ctx.strokeStyle = c.ink; ctx.stroke(); }
          return;
        }
        ctx.beginPath(); ctx.arc(x, y, b.r, 0, TAU);
        ctx.fillStyle = c.paper; ctx.fill();
        ctx.lineWidth = 1.4; ctx.strokeStyle = c.ink; ctx.stroke();
        const name = b.p.label;
        const per = narrow ? '' : `${f1.format(b.p.P)} d`;
        ctx.font = `500 11px ${c.mono}`;
        const wn = ctx.measureText(name).width;
        ctx.font = `400 11px ${c.mono}`;
        const wp = ctx.measureText(per).width;
        const total = Math.max(wn + 8 + wp, sorten && b.p.hz ? 80 : 0);
        const right = x + 10 + total < w - 8;
        const lx = right ? x + 10 : x - 10 - total;
        let ly = y - 11;
        if (ly < 12) ly = y + 13;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';
        ctx.font = `500 11px ${c.mono}`;
        ctx.fillStyle = c.ink;
        ctx.fillText(name, lx, ly);
        ctx.font = `400 11px ${c.mono}`;
        ctx.fillStyle = c.ink2;
        ctx.fillText(per, lx + wn + 8, ly);
        if (sorten && b.p.hz) this._tag(ctx, 'habitat', 'Habitat', lx, ly + (ly < y ? -16 : 16));
      });

      // Doppelstern: Beschriftung mit Hinweislinie (schmal: nach links unten)
      ctx.strokeStyle = c.muted;
      ctx.lineWidth = 1;
      ctx.textBaseline = 'middle';
      if (narrow) {
        const ex = cx - 30;
        const ey = cy + 30;
        ctx.beginPath(); ctx.moveTo(cx - 10, cy + 8); ctx.lineTo(ex, ey); ctx.lineTo(ex - 8, ey); ctx.stroke();
        ctx.font = `500 11px ${c.mono}`;
        ctx.fillStyle = c.ink;
        ctx.textAlign = 'right';
        ctx.fillText('A + B', ex - 12, ey);
        ctx.textAlign = 'left';
        if (sorten) this._tag(ctx, 'tandem', 'Tandem', ex - 12, ey + 16, 'right');
      } else {
        const lead = top ? 44 : 38;
        const ex = cx + lead;
        const ey = cy - lead - (top ? 6 : 14);
        ctx.beginPath(); ctx.moveTo(cx + 12, cy - 12); ctx.lineTo(ex, ey); ctx.lineTo(ex + 10, ey); ctx.stroke();
        ctx.textAlign = 'left';
        ctx.font = `500 11px ${c.mono}`;
        ctx.fillStyle = c.ink;
        ctx.fillText('A + B', ex + 14, ey);
        const lw = ctx.measureText('A + B').width;
        ctx.font = `400 11px ${c.mono}`;
        ctx.fillStyle = c.ink2;
        ctx.fillText(`${f2.format(BIN.P)} d`, ex + 14 + lw + 8, ey);
        if (sorten) this._tag(ctx, 'tandem', 'Tandem', ex + 14, ey - 16);
      }

      // Eklipse: kurzes Aufleuchten bei jeder Bedeckung
      if (sorten && performance.now() < this._flashUntil) {
        const k = (this._flashUntil - performance.now()) / 480;
        ctx.globalAlpha = Math.max(0, Math.min(1, k * 1.4));
        ctx.strokeStyle = c.eklipse;
        ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(cx, cy, 26, 0, TAU); ctx.stroke();
        this._tag(ctx, 'eklipse', 'Eklipse', cx - 34, cy + 34, 'right');
        ctx.globalAlpha = 1;
      }
    }

    _drawSkyDome() {
      if (!this.dome || this.$skyblock.hidden) return;
      const { w, h, dpr } = this.dome;
      const ctx = this.$skyc.getContext('2d');
      const c = this.colors;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      const R = w / 2 - 16;
      const cx = w / 2;
      const cy = h / 2;
      const pt = (alt, az) => {
        const r = (R * (90 - alt)) / 90;
        return [cx - r * Math.sin(az * RAD), cy - r * Math.cos(az * RAD)]; // N oben, O links (Blick nach oben)
      };
      // Horizont und Höhenkreise
      ctx.lineWidth = 1;
      ctx.strokeStyle = c.ink2;
      ctx.beginPath(); ctx.arc(cx, cy, R, 0, TAU); ctx.stroke();
      ctx.strokeStyle = c.line;
      ctx.setLineDash([2, 3]);
      [30, 60].forEach((a) => { ctx.beginPath(); ctx.arc(cx, cy, (R * (90 - a)) / 90, 0, TAU); ctx.stroke(); });
      ctx.setLineDash([]);
      // Meridian (Nord–Süd)
      ctx.strokeStyle = c.meridian;
      ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(cx, cy - R); ctx.lineTo(cx, cy + R); ctx.stroke();
      // Tagesbahn von Kepler-47
      const s = sky(this.lat, this.lon, Date.now());
      ctx.strokeStyle = c.muted;
      ctx.lineWidth = 1;
      ctx.setLineDash([1, 3]);
      ctx.beginPath();
      for (let i = 0; i <= 180; i++) {
        const H = (i / 180) * 360;
        const p = altAz(this.lat, s.dec, H);
        const [x, y] = pt(p.alt, p.az);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.setLineDash([]);
      // Zenit
      ctx.fillStyle = c.zenit;
      ctx.fillRect(cx - 4, cy - 4, 8, 8);
      // Kepler-47 jetzt
      const [kx, ky] = pt(s.alt, s.az);
      ctx.globalAlpha = c.light ? 0.3 : 0.22;
      ctx.fillStyle = c.accent;
      ctx.beginPath(); ctx.arc(kx, ky, 9, 0, TAU); ctx.fill();
      ctx.globalAlpha = 1;
      ctx.beginPath(); ctx.arc(kx, ky, 4, 0, TAU); ctx.fill();
      if (c.light) { ctx.strokeStyle = c.ink; ctx.lineWidth = 1; ctx.stroke(); }
      // Himmelsrichtungen
      ctx.font = `500 10px ${c.mono}`;
      ctx.fillStyle = c.ink2;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('N', cx, cy - R - 9);
      ctx.fillText('S', cx, cy + R + 9);
      ctx.fillText('O', cx - R - 9, cy);
      ctx.fillText('W', cx + R + 9, cy);
      // Beschriftung
      ctx.textAlign = 'left';
      ctx.fillStyle = c.ink;
      ctx.fillText('Zenit', cx + 8, cy + 10);
      ctx.save();
      ctx.translate(cx - 6, cy + R * 0.62);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.fillText('Meridian', 0, 0);
      ctx.restore();
      ctx.textAlign = 'left';
      const lbl = 'Kepler-47';
      const lx = kx + 12 + ctx.measureText(lbl).width > w ? kx - 12 - ctx.measureText(lbl).width : kx + 12;
      ctx.fillText(lbl, lx, ky - 10);
    }

    _drawCurve() {
      const { w, h, dpr } = this.lc;
      const ctx = this.$lc.getContext('2d');
      const c = this.colors;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      const L = 46;
      const R = 6;
      const T = 16;
      const B = 18;
      const pw = w - L - R;
      const ph = h - T - B;
      const t1 = this.t;
      const t0 = t1 - WINDOW;
      const xOf = (t) => L + ((t - t0) / WINDOW) * pw;
      const yOf = (f) => T + (1 - (f - FLUX_MIN) / (FLUX_MAX - FLUX_MIN)) * ph;

      ctx.font = `400 10px ${c.mono}`;
      ctx.textBaseline = 'middle';
      ctx.lineWidth = 1;
      [[1, '100 %', []], [0.9, '90 %', [2, 3]]].forEach(([f, label, dash]) => {
        const y = Math.round(yOf(f)) + 0.5;
        ctx.strokeStyle = c.line;
        ctx.setLineDash(dash);
        ctx.beginPath(); ctx.moveTo(L, y); ctx.lineTo(w - R, y); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = c.ink2;
        ctx.textAlign = 'right';
        ctx.fillText(label, L - 8, y);
      });
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = c.ink2;
      ctx.textAlign = 'left';
      ctx.fillText(`−${WINDOW} d`, L, h - 4);
      ctx.textAlign = 'center';
      ctx.fillText(`−${WINDOW / 2} d`, L + pw / 2, h - 4);
      ctx.textAlign = 'right';
      ctx.fillText('jetzt', w - R, h - 4);

      ctx.font = `500 10px ${c.mono}`;
      let lastLabelX = -1e9;
      let lastLabel = '';
      this.events.forEach((e) => {
        if (e.t < t0 || e.t > t1) return;
        const x = Math.round(xOf(e.t)) + 0.5;
        ctx.strokeStyle = c.ink2;
        ctx.beginPath(); ctx.moveTo(x, T - 2); ctx.lineTo(x, yOf(1) - 4); ctx.stroke();
        if ((x - lastLabelX > 14 || e.planet !== lastLabel) && x - lastLabelX > 9) {
          ctx.fillStyle = c.ink;
          ctx.textAlign = 'center';
          ctx.fillText(e.planet, x, T - 5);
          lastLabelX = x;
          lastLabel = e.planet;
        }
      });
      ctx.textAlign = 'left';

      ctx.beginPath();
      let first = true;
      for (let i = 0; i < this.samples.length; i++) {
        const s = this.samples[i];
        if (s.t < t0) continue;
        const x = xOf(s.t);
        const y = yOf(s.f);
        if (first) { ctx.moveTo(x, y); first = false; } else ctx.lineTo(x, y);
      }
      ctx.lineWidth = 1.25;
      ctx.strokeStyle = c.ink;
      ctx.lineJoin = 'round';
      ctx.stroke();
      const lastS = this.samples[this.samples.length - 1];
      if (lastS) {
        ctx.fillStyle = c.accent;
        ctx.beginPath(); ctx.arc(xOf(lastS.t), yOf(lastS.f), 3, 0, TAU); ctx.fill();
      }
    }
  }

  customElements.define('k47-orbit', K47Orbit);
})();
